/* 
🎯 Shell UI Design System v3.0 - Glass Container Component
Purpose: Glassmorphic containers with distorted patterns and brutalist styling
Philosophy: Honest materials, functional surfaces, subtle sophistication
*/

import React from 'react';

export const GlassContainer = ({ 
  children, 
  variant = 'default',
  size = 'default',
  pattern = 'subtle',
  className = '',
  animate = true,
  enhanced = false,  // Enhanced glass effect for dialogs/modals
  ...props 
}) => {
  const baseClass = 'glassmorphic-section';
  const variantClass = variant !== 'default' ? `${baseClass}--${variant}` : '';
  const sizeClass = size !== 'default' ? `${baseClass}--${size}` : '';
  const patternClass = pattern !== 'subtle' ? `${baseClass}--pattern-${pattern}` : '';
  const animateClass = animate ? `${baseClass}--animate` : '';
  
  const combinedClassName = [
    baseClass, 
    variantClass, 
    sizeClass, 
    patternClass,
    animateClass,
    className
  ].filter(Boolean).join(' ');

  // Enhanced glass effects for dialogs and modals (always above background)
  const enhancedStyle = enhanced ? {
    position: 'relative',
    zIndex: 1001,
    background: 'rgba(255, 255, 255, 0.25)',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    borderRadius: '16px',
    backdropFilter: 'blur(24px) saturate(180%)',
    WebkitBackdropFilter: 'blur(24px) saturate(180%)',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.4)',
    ...props.style
  } : props.style;

  return (
    <div 
      className={combinedClassName} 
      {...props}
      style={enhancedStyle}
    >
      {children}
    </div>
  );
};

export const GlassCard = ({ 
  children, 
  title,
  subtitle,
  icon,
  className = '', 
  ...props 
}) => {
  return (
    <GlassContainer 
      variant="card" 
      className={`glass-card ${className}`} 
      {...props}
    >
      {(title || subtitle || icon) && (
        <div className="glass-card__header">
          {icon && (
            <div className="glass-card__icon">
              {icon}
            </div>
          )}
          <div className="glass-card__text">
            {title && (
              <h3 className="glass-card__title text-title">
                {title}
              </h3>
            )}
            {subtitle && (
              <p className="glass-card__subtitle text-body">
                {subtitle}
              </p>
            )}
          </div>
        </div>
      )}
      <div className="glass-card__content">
        {children}
      </div>
    </GlassContainer>
  );
};

export const MetricCard = ({ 
  value, 
  label, 
  trend,
  trendDirection,
  icon,
  status = 'neutral',
  className = '', 
  ...props 
}) => {
  const getStatusClass = () => {
    switch (status) {
      case 'success':
      case 'positive':
        return 'metric-card--success';
      case 'error':
      case 'negative':
        return 'metric-card--error';
      case 'warning':
        return 'metric-card--warning';
      default:
        return 'metric-card--neutral';
    }
  };

  return (
    <GlassContainer 
      variant="metric" 
      className={`metric-card ${getStatusClass()} ${className}`}
      {...props}
    >
      <div className="metric-card__header">
        {icon && (
          <div className="metric-card__icon">
            {icon}
          </div>
        )}
        {trend && (
          <div className={`metric-card__trend metric-card__trend--${trendDirection || 'up'}`}>
            {trend}
          </div>
        )}
      </div>
      
      <div className="metric-card__value text-dotmatrix">
        {value}
      </div>
      
      <div className="metric-card__label text-label">
        {label}
      </div>
    </GlassContainer>
  );
};

export const ShellContainer = ({ 
  children,
  shellName,
  status = 'offline',
  uptime,
  className = '',
  ...props 
}) => {
  return (
    <GlassContainer 
      variant="large" 
      className={`shell-container ${className}`}
      animate={true}
      {...props}
    >
      <div className="shell-container__content">
        {children}
      </div>
      
      {(shellName || status || uptime) && (
        <div className="shell-container__footer">
          {shellName && (
            <div className="shell-container__name text-label">
              {shellName}
            </div>
          )}
          
          {status && (
            <div className="shell-container__status">
              <StatusIndicator status={status} showText={false} />
            </div>
          )}
          
          {uptime && (
            <div className="shell-container__uptime text-label">
              {uptime}
            </div>
          )}
        </div>
      )}
    </GlassContainer>
  );
};

export const GlassSection = ({ 
  children,
  title,
  subtitle,
  headerActions,
  collapsible = false,
  collapsed = false,
  onToggle,
  className = '',
  ...props 
}) => {
  const [isCollapsed, setIsCollapsed] = React.useState(collapsed);

  const handleToggle = () => {
    const newState = !isCollapsed;
    setIsCollapsed(newState);
    if (onToggle) onToggle(newState);
  };

  return (
    <GlassContainer 
      className={`glass-section ${isCollapsed ? 'glass-section--collapsed' : ''} ${className}`}
      {...props}
    >
      {(title || subtitle || headerActions || collapsible) && (
        <div className="glass-section__header">
          <div className="glass-section__text">
            {title && (
              <h2 className="glass-section__title text-title">
                {title}
              </h2>
            )}
            {subtitle && (
              <p className="glass-section__subtitle text-body">
                {subtitle}
              </p>
            )}
          </div>
          
          <div className="glass-section__actions">
            {headerActions}
            {collapsible && (
              <button 
                className="glass-section__toggle shell-btn shell-btn--ghost"
                onClick={handleToggle}
                aria-expanded={!isCollapsed}
              >
                {isCollapsed ? '▼' : '▲'}
              </button>
            )}
          </div>
        </div>
      )}
      
      <div className={`glass-section__content ${isCollapsed ? 'glass-section__content--collapsed' : ''}`}>
        {children}
      </div>
    </GlassContainer>
  );
};

export const GlassGrid = ({ 
  children,
  columns = 'auto-fit',
  gap = 'default',
  minWidth = '280px',
  className = '',
  ...props 
}) => {
  const baseClass = 'glass-grid';
  const gapClass = gap !== 'default' ? `${baseClass}--gap-${gap}` : '';
  
  const combinedClassName = [baseClass, gapClass, className]
    .filter(Boolean)
    .join(' ');

  const gridStyle = {
    gridTemplateColumns: typeof columns === 'number' 
      ? `repeat(${columns}, 1fr)`
      : `repeat(${columns}, minmax(${minWidth}, 1fr))`
  };

  return (
    <div 
      className={combinedClassName}
      style={gridStyle}
      {...props}
    >
      {children}
    </div>
  );
};

export default GlassContainer;

/*
USAGE EXAMPLES:

Basic glass container:
<GlassContainer>
  <p>Content with glassmorphic background</p>
</GlassContainer>

Glass card with header:
<GlassCard 
  title="Server Status"
  subtitle="Production environment"
  icon={<ServerIcon />}
>
  <StatusIndicator status="online" showText={true} />
</GlassCard>

Metric card for dashboard:
<MetricCard 
  value="142"
  label="Active Connections"
  trend="+12%"
  trendDirection="up"
  status="success"
  icon={<ConnectionIcon />}
/>

Shell container for placeholder app:
<ShellContainer
  shellName="main-server"
  status="online"
  uptime="04:23:15"
>
  <DotMatrix value="SHELL_READY" />
  <StatusIndicator status="online" showText={true} />
</ShellContainer>

Collapsible section:
<GlassSection
  title="Configuration"
  subtitle="Server settings and options"
  collapsible={true}
  headerActions={
    <Button variant="ghost" size="small">Edit</Button>
  }
>
  <form>
    Configuration form content
  </form>
</GlassSection>

Grid layout for multiple cards:
<GlassGrid columns={3} gap="large">
  <MetricCard value="45%" label="CPU Usage" status="success" />
  <MetricCard value="72%" label="Memory" status="warning" />
  <MetricCard value="23%" label="Storage" status="success" />
</GlassGrid>

Dashboard layout example:
const Dashboard = () => (
  <div className="dashboard">
    <GlassSection title="System Overview">
      <GlassGrid columns="auto-fit" minWidth="200px">
        <MetricCard value="99.9%" label="Uptime" status="success" />
        <MetricCard value="1,247" label="Requests/min" status="success" />
        <MetricCard value="2" label="Alerts" status="error" />
        <MetricCard value="8" label="Active Shells" status="success" />
      </GlassGrid>
    </GlassSection>
    
    <GlassGrid columns={2} gap="large">
      <GlassCard title="Recent Activity">
        <Terminal lines={recentLogs} />
      </GlassCard>
      
      <GlassCard title="System Health">
        <StatusGroup orientation="vertical">
          <StatusIndicator status="online" text="Web Server" showText={true} />
          <StatusIndicator status="online" text="Database" showText={true} />
          <StatusIndicator status="warning" text="Cache" showText={true} />
        </StatusGroup>
      </GlassCard>
    </GlassGrid>
  </div>
);

FEATURES:
- Multiple container variants (default, card, metric, large, section)
- Glassmorphic effects with backdrop-filter
- Distorted line patterns for texture
- Built-in animation support
- Responsive grid system
- Collapsible sections
- Metric cards with trend indicators
- Shell-specific container variant
- Header/footer support
- Icon and action support

PHILOSOPHY ALIGNMENT:
- Honest materials - glass effects suggest transparency
- Functional surfaces - containers serve content organization
- Subtle sophistication - refined without being decorative
- Brutalist approach - structure follows function
- Minimal decoration - patterns are subtle and purposeful

ACCESSIBILITY:
- Proper semantic structure
- ARIA attributes for interactive elements
- Keyboard navigation support
- High contrast maintained through glass effects
- Focus management for collapsible sections

CSS INTEGRATION:
Uses .glassmorphic-section base classes from components.css with:
- backdrop-filter for glass effects
- Distorted line patterns via CSS gradients
- Responsive behavior built-in
- Animation support for entrance effects
*/